/* ============================================
   PROPERTYLISTINGS.JSX
   ============================================ */
import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import {
  Home, CheckCircle, FileText, X, Eye, Edit, Trash2,
  Plus, Upload, Image as ImageIcon, MapPin, Phone, Mail,
  MoreVertical, Tag, Power, ArrowLeft, Calendar, Maximize2
} from 'lucide-react';
import { FaClock, FaHome } from 'react-icons/fa';
import '../../../assets/css/sellers/dashboard/sellerproperty/myproperty.css';
import SellerMasterLayout from 'layouts/SellerMasterLayout';
import AppContext from 'context/AppContext';
import Swal from 'sweetalert2';
import { GiLongLeggedSpider } from 'react-icons/gi';
import PropertyViewScreen from 'subscreens/Seller/viewscreen/PropertyViewScreen';


/* ============================================================
   CONFIRMATION MODAL COMPONENT
   ============================================================ */
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText, confirmColor = '#EF4444', icon }) {
  if (!isOpen) return null;
  return (
    <div className="confirm-overlay" onClick={onClose}>
      <div className="confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="confirm-icon-wrap" style={{ backgroundColor: `${confirmColor}15` }}>
          {icon}
        </div>
        <h3 className="confirm-title">{title}</h3>
        <p className="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button className="confirm-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="confirm-action-btn"
            style={{ backgroundColor: confirmColor, borderColor: confirmColor }}
            onClick={() => { onConfirm(); onClose(); }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}



/* ============================================================
   MAIN COMPONENT
   ============================================================ */
function MyProperty() {
  
  const { baseUrl, baseUrlForFiles, Navigate, authToken,loggedInSeller } = useContext(AppContext);
  const [propertyForEdit, setPropertyForEdit] = useState(null);
  const [textForEditAddProperty, setTextForEditAddProperty] = useState("Add");

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [activeTypeFilter, setActiveTypeFilter] = useState('All');
  const [viewingProperty, setViewingProperty] = useState(null);
  const menuRef = useRef(null);

  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const [propertyId, setPropertyId] = useState('');

  const [property_title, setproperty_title] = useState('');
  const [property_Type, setProperty_Type] = useState('');
  const [listing_Type, setListing_Type] = useState('FOR SALE');
  const [size, setSize] = useState('');
  const [location, setLocation] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [parking_Spaces, setParking_Spaces] = useState('');
  const [tableLoading, setTableLoading] = useState(true);


  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, type: '', propertyId: null,
    title: '', message: '', confirmText: '', confirmColor: '#EF4444', icon: null,
  });

const handleClick = () => {
  axios.get(
    `${baseUrl}/seller/sellerkyc/getMyKycStatus`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    }
  )
  .then((response) => {
    if (response.data.success) {
      const { hasKyc, status } = response.data;
      // KYC APPROVED
      if (hasKyc && status === "Approved") {
        Navigate("/seller/addproperty");
        return;
      }
      // KYC NOT APPROVED
      let message =
        "Please complete and get your KYC verification approved before adding or publishing a property.";
      if (status === "Pending") {
        message =
          "Your KYC verification is currently under review. You can add a property once your KYC is approved.";
      }
      if (status === "Rejected") {
        message =
          "Your KYC verification was rejected. Please complete your KYC verification again before adding a property.";
      }
      // KYC REQUIRED ALERT
      Swal.fire({
        icon: "info",
        title: "KYC Verification Required",
        text: message,
        confirmButtonText: "Complete KYC",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonColor: "#000000",
        cancelButtonColor: "#ffffff",
        reverseButtons: true,
        customClass: {
          popup: "estate-kyc-alert",
          title: "estate-kyc-alert-title",
          htmlContainer: "estate-kyc-alert-text",
          confirmButton: "estate-kyc-alert-confirm",
          cancelButton: "estate-kyc-alert-cancel"
        }
      }).then((result) => {
        if (result.isConfirmed) {
          Navigate("/seller/kyc/verify");
        }
      });
    }
  })
  .catch((error) => {
    console.log("KYC status error:", error);
    if (error.response?.status === 401) {
      Swal.fire({
        icon: "warning",
        title: "Session Expired",
        text: "Please login again to continue.",
        confirmButtonColor: "#000000"
      });
      return;
    }
    Swal.fire({
      icon: "error",
      title: "Something went wrong",
      text: "Unable to verify your KYC status. Please try again.",
      confirmButtonColor: "#000000"
    });
  });
}; 
  useEffect(() => { setAnimateCards(true); }, []);
  // ── Fetch all properties ──
  const viewAllProperties = () => {
    setTableLoading(true);
    axios
      .get(`${baseUrl}/seller/sellerproperties/viewAllProperties`, {
          headers: {
            "Content-Type": "multipart/formdata",
            "Authorization": `Bearer ${authToken}`
          },
        })
      .then((response) => {
        console.log(response.data);
        setProperties(response.data.property || []);
        setTableLoading(false);
      })
      .catch((error) => {
        console.log('Error fetching properties:', error);
        setTableLoading(false);
      });
  };

  useEffect(() => {
    viewAllProperties();
  }, []);

  // ── Image Upload Handlers ──
  const handleImageDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleMultipleImageFiles(Array.from(e.dataTransfer.files));
  };
  const handleImageSelect = (e) => handleMultipleImageFiles(Array.from(e.target.files));

  const handleMultipleImageFiles = (files) => {
    const validImages = files.filter(f => f.type.startsWith('image/'));
    if (uploadedImages.length + validImages.length > 3) {
      alert('Max 3 images allowed');
      return;
    }
    validImages.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImages(prev => prev.length < 3 ? [...prev, e.target.result] : prev);
      };
      reader.readAsDataURL(file);
    });
    setImageFiles(prev => [...prev, ...validImages].slice(0, 3));
  };

  const removeImage = (i) => {
    setUploadedImages(prev => prev.filter((_, idx) => idx !== i));
    setImageFiles(prev => prev.filter((_, idx) => idx !== i));
  };

  // ── Filtering ──
  const getFilteredProperties = () => {
    let filtered = [...properties];
    if (activeTypeFilter !== 'All') filtered = filtered.filter(p => p.type === activeTypeFilter);
    return filtered;
  };


  // ── Temp dummy action functions ──
  const executeDelete = () => { console.log('Delete function pending...'); };
  const executeToggle = () => { console.log('Toggle function pending...'); };
  const executeSold = () => { console.log('Sold function pending...'); };
  const resetAddForm = () => { console.log('Reset form pending...'); };

  // const openSoldConfirm = (property) => {
  //   console.log('Open sold confirm', property);
  // };


  const deleteProperty = (propertyId) => {
    axios.post(`${baseUrl}/seller/sellerproperties/deleteProperty`,  {
       propertyId: propertyId,
          headers: {
            "Content-Type": "multipart/formdata",  
            "Authorization": `Bearer ${authToken}`
          },
        })
      .then((response) => {
        if (response.data.success === true) {
          Swal.fire("Deleted!", "Property deleted successfully", "success");
          viewAllProperties();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const handleEditSubmit = () => {
    console.log('Edit submit pending...');
  };

  // ── Confirm router ──
  const handleConfirmAction = () => {
    if (confirmModal.type === 'delete') executeDelete();
    else if (confirmModal.type === 'toggle') executeToggle();
    else if (confirmModal.type === 'sold') executeSold();
  };

  // ── View click ──
 const handleViewClick = (property) => {
  setViewingProperty(property);
};
  const openAddPropertyModal = () => {
    resetAddForm();
    setShowAddPropertyModal(true);
  };

  // ── Helper colors ──
  const getTypeColor = (type) => {
    switch (type) {
      case 'House': return '#10B981';
      case 'Apartment': return '#17a2b8';
      case 'Villa': return '#8b5cf6';
      case 'Plot': return '#22c55e';
      case 'Commercial': return '#d97706';
      case 'Farm House': return '#b45309';
      default: return '#4a4a4a';
    }
  };
  const getListingColor = (listingType) => listingType === 'FOR SALE' ? '#10B981' : '#17a2b8';
  const getStatusColor = (status) => {
    if (status === 'Active') return '#10B981';
    if (status === 'Sold' || status === 'Rented') return '#8b5cf6';
    if (status === 'Pending') return '#d97706';
    return '#EF4444';
  };


  const filteredProperties = getFilteredProperties();

  const statsData = [
    { id: 1, icon: Home, color: '#8b5cf6', value: properties.length.toString(), label: 'Total Properties' },
    { id: 2, icon: CheckCircle, color: '#ec4899', value: properties.filter(p => p.status === 'Active').length.toString(), label: 'Active Listings' },
    { id: 3, icon: FileText, color: '#10b981', value: properties.filter(p => p.status === 'Inactive').length.toString(), label: 'Inactive Listings' },
  ];

  // ── Dynamic Form Fields ──
  const renderDynamicFields = () => {
    switch (property_Type) {
      case 'House':
      case 'Apartment':
      case 'Villa':
      case 'Farm House':
        return (
          <div className="form-row-three">
            <div className="form-group">
              <label className="Poppins-Medium">Bedrooms *</label>
              <input type="number" className="form-input" placeholder="e.g., 3"
                value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="Poppins-Medium">Bathrooms *</label>
              <input type="number" className="form-input" placeholder="e.g., 2"
                value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="Poppins-Medium">Parking Spaces</label>
              <input type="number" className="form-input" placeholder="e.g., 2"
                value={parking_Spaces} onChange={(e) => setParking_Spaces(e.target.value)} />
            </div>
          </div>
        );
      case 'Commercial':
        return (
          <div className="form-row">
            <div className="form-group">
              <label className="Poppins-Medium">Parking Spaces *</label>
              <input type="number" className="form-input" placeholder="e.g., 5"
                value={parking_Spaces} onChange={(e) => setParking_Spaces(e.target.value)} required />
            </div>
          </div>
        );
      case 'Plot':
        return null;
      default:
        return (
          <div className="form-group">
            <p className="Poppins-Regular" style={{ color: '#767676', textAlign: 'center', padding: '20px' }}>
              Please select a property type to see relevant fields
            </p>
          </div>
        );
    }
  };

  if (viewingProperty) {
  return (
    <PropertyViewScreen
      property={viewingProperty}
      isAdmin={false}
      onBack={() => setViewingProperty(null)}
    />
  );
}
  /* ====================================================
     MAIN RENDER
     ==================================================== */
  return (
    <SellerMasterLayout pageTitle="My Property">
      <div className="property-management-page">

        {/* PAGE HEADER */}
        <div className="property-header">
          <div style={{ flex: 1 }} />
          <div className="add-property-buttons-group">
            {/* <button className="add-property-btn add-all-btn Poppins-Medium" onClick={openAddPropertyModal}> */}
            <button className="add-property-btn add-all-btn Poppins-Medium" onClick={handleClick}>
              <Plus size={20} /> Add Property
            </button>
            {/* <button
            className="add-property-btn add-all-btn"
           onClick={() => {
           if (!isKYCVerified) {
            alert("Please complete your KYC before adding property.");
            return;
        }
        openAddPropertyModal();
    }}
      ><Plus size={20} />Add Property  
      </button> */}
            {/* const isSkipped = localStorage.getItem("seller_kyc_skipped") === "true";*/}

            {/* <button
  className="add-property-btn add-all-btn"
  onClick={() => {

    const verified =
      localStorage.getItem("seller_kyc_verified") === "true";

    if (isSkipped || !verified) {
      alert("Please complete your KYC before adding property.");
      return;
    }

    handleClick();
  }}
>
  <Plus size={20} />
  Add Property
</button> */}
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="property-stats-grid">
          {statsData.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.id}
                className={`property-stat-card ${animateCards ? 'animate' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-icon-circle" style={{ background: `${stat.color}20`, color: stat.color }}>
                  <Icon size={24} />
                </div>
                <div className="stat-details">
                  <h2 className="stat-number Baloo2-Bold">{stat.value}</h2>
                  <p className="stat-label Poppins-Regular">{stat.label}</p>
                </div>
              </div>
            );
          })}
        </div>



        {/* PROPERTIES TABLE — Matches fillable columns & brand kit */}
        <div className="properties-table-container">
          <table className="properties-table">
            <thead>
              <tr>
                <th className="Poppins-Medium">#</th>
                <th className="Poppins-Medium">IMAGE</th>
                <th className="Poppins-Medium">TITLE</th>
                <th className="Poppins-Medium">PROPERTY TYPE</th>
                <th className="Poppins-Medium">LISTING TYPE</th>

                <th className="Poppins-Medium">PRICE</th>
                <th className="Poppins-Medium">CITY</th>
                <th className="Poppins-Medium">STATUS</th>
                <th className="Poppins-Medium" style={{ textAlign: 'center' }}>VIEWS</th>
                <th className="Poppins-Medium" style={{ textAlign: 'center' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="10">
                    <div className="kyc-table-loader">
                      <div className="kyc-table-loader-spinner"></div>
                      <h3>Loading Properties Records...</h3>
                      <p>Please wait while data is loading</p>
                    </div>
                  </td>
                </tr>
              ) : filteredProperties.length === 0 ? (
                <tr>
                  <td colSpan="10">
                    <div className="no-properties-message">
                      <Home size={48} color="#CBD5E1" />
                      <h3 className="Baloo2-Bold">No properties found</h3>
                      <p className="Poppins-Regular">Try adjusting your filters or add a new property</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredProperties.map((property, index) => {
                  const propId = property.id || property.property_id;
                  const typeColor = getTypeColor(property.property_type);
                  const listingColor = getListingColor(property.listingType);
                  const statusColor = getStatusColor(property.status);

                  return (
                    <tr key={propId}>
                      {/* # */}
                      <td>
                        <span className="property-row-index Poppins-Medium">{index + 1}</span>
                      </td>

                      {/* Image */}
                      <td>
                        <img
                          src={
                            property.expropertyimages?.length > 0
                              ? `${baseUrlForFiles}${property.expropertyimages[0].images}`
                              : (property.image
                                ? `${baseUrlForFiles}${property.image}`
                                : 'https://via.placeholder.com/400x300?text=No+Image')
                          }
                          alt={property.property_title}
                          className="property-image"
                        />
                      </td>

                      {/* Title (with location as subtitle) */}
                      <td>
                        <div className="property-title-section">
                          <h3 className="property-title Poppins-Medium">{property.property_title}</h3>
                          {property.location && (
                            <p className="property-subtitle Poppins-Regular">{property.location}</p>
                          )}
                        </div>
                      </td>

                      {/* Type */}
                      <td>
                        <span
                          className="property-type-badge Poppins-Medium"
                          style={{ backgroundColor: `${typeColor}20`, color: typeColor }}
                        >
                          {property.property_type}
                        </span>
                      </td>

                      {/* Listing Type */}
                      <td>
                        <span className="property-listing-text Poppins-Regular">
                          {property.listingType === 'FOR SALE' ? 'For Sale' : 'For Rent'}
                        </span>
                      </td>

                      {/* Price */}
                      <td>
                        <span className="property-price-cell Poppins-Bold">{property.price}</span>
                      </td>

                      {/* City */}
                      <td>
                        <span className="property-location Poppins-Regular">{property.city}</span>
                      </td>

                      {/* Status */}
                      <td>
                        <span
                          className="property-status-badge Poppins-Medium"
                          style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
                        >
                          {property.status}
                        </span>
                      </td>

                      {/* Views */}
                      <td style={{ textAlign: 'center' }}>
                        <span className="property-views-count Poppins-Medium">{property.views || 0}</span>
                      </td>

                      {/* ✅ ACTIONS — Eye, Edit, Delete icon buttons */}
                      <td>
                        <div className="table-icon-actions">
                          <button
                            className="table-icon-btn view"
                            title="View"
                            onClick={() =>
                              handleViewClick(property)
                            }
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            className="table-icon-btn edit"
                            title="Edit"
                            onClick={() => {
                              Navigate('/seller/addproperty', { state: { property, mode: 'edit' } });
                            }}
                          >
                            <Edit size={15} />
                          </button>
                          <button
                            className="table-icon-btn delete"
                            title="Delete"
                            onClick={() => deleteProperty(propId)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* CONFIRMATION MODAL */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          onConfirm={handleConfirmAction}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          confirmColor={confirmModal.confirmColor}
          icon={confirmModal.icon}
        />
        {/* <AddProperty
        viewAllProperties={viewAllProperties}
        /> */}

      </div>
    </SellerMasterLayout>
  );
}

export default MyProperty;